import { useEffect, useState, useCallback } from 'react';
import { TierConfigViewModel, createTierConfigViewModel } from '../../../schemas/FundWaterfall.schema';
import { WaterfallTiersConfigProps } from './WaterfallTiersConfig';

/**
 * Copies the server's tier ids onto the tiers on screen, but only when the two lists describe the
 * same tiers: same length, same positions, same names. Anything else means there are local edits
 * the server has not seen yet, and pairing by position would hand a tier another tier's identity.
 * Skipping is safe because the pending edit saves within a second and brings the lists back in step.
 *
 * `id` is deliberately left alone. It is the list and drag key, so changing it remounts the row,
 * which steals focus mid-rename and drops an in-progress drag.
 *
 * Returns the same array when there is nothing to copy, to avoid a pointless render.
 */
export function adoptSavedTierIds(
  current: TierConfigViewModel[],
  saved: TierConfigViewModel[]
): TierConfigViewModel[] {
  if (current.length !== saved.length) {
    return current;
  }

  const describesSameTiers = current.every(
    (tier, i) => tier.orderNumber === saved[i].orderNumber && tier.tierName === saved[i].tierName
  );
  if (!describesSameTiers) {
    return current;
  }

  let adopted = false;
  const next = current.map((tier, i) => {
    const savedTierId = saved[i].tierId;
    if (!savedTierId || tier.tierId === savedTierId) {
      return tier;
    }
    adopted = true;
    return { ...tier, tierId: savedTierId };
  });

  return adopted ? next : current;
}

export function useWaterfallTiersConfig(props: WaterfallTiersConfigProps) {
  const { tiers: initialTiers, onSave } = props;
  const [formTierData, setFormTierData] = useState<TierConfigViewModel[]>(() => initialTiers);

  // A tier added here has no server id until the save comes back. Without copying it over, the
  // next save sends no identity and the server replaces the row instead of moving it.
  useEffect(() => {
    setFormTierData((current) => adoptSavedTierIds(current, initialTiers));
  }, [initialTiers]);

  const onRename = useCallback(
    (tier: TierConfigViewModel) => {
      const newTiers = formTierData.map((t) => (t.id === tier.id ? { ...t, tierName: tier.tierName } : t));
      setFormTierData(newTiers);
      onSave(newTiers);
    },
    [formTierData, onSave]
  );
  const onDelete = useCallback(
    (tier: TierConfigViewModel) => {
      const newTierData = formTierData.reduce((acc, t) => {
        if (t.id === tier.id) {
          return acc;
        }
        const newTierOrder = acc.length + 1;
        acc.push({ ...t, orderNumber: newTierOrder });
        return acc;
      }, [] as TierConfigViewModel[]);
      setFormTierData(newTierData);
      onSave(newTierData);
    },
    [formTierData, onSave]
  );

  const onAdd = useCallback(() => {
    const nextSortOrder = formTierData.length + 1;
    const newTierData = [
      ...formTierData,
      createTierConfigViewModel({ orderNumber: nextSortOrder, tierName: `Tier ${nextSortOrder}` }),
    ];
    setFormTierData(newTierData);
    onSave(newTierData);
  }, [formTierData, onSave]);

  const onReorder = useCallback(
    (tiers: TierConfigViewModel[]) => {
      const newTierData = tiers.map((t, i) => ({ ...t, orderNumber: i + 1 }));
      setFormTierData(newTierData);
      onSave(newTierData);
    },
    [onSave]
  );

  return { onRename, onDelete, onAdd, onReorder, formTierData, setFormTierData };
}
